import React, { useState } from "react";
import { ServicePageHero, FeatureGrid, ProcessSteps, CTAStripe } from "@/components/sections/Shared";
import QuoteDialog from "@/components/QuoteDialog";
import { IMAGES } from "@/lib/business";
import { Building2, ShieldCheck, Eye, Sun, Sparkles, Layers } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import SEO, { serviceJsonLd } from "@/components/SEO";

const FEATURES = [
    { icon: ShieldCheck, title: "Storefront Security Film", description: "Delay forced entry, protect merchandise, and keep shattered glass in place." },
    { icon: Sun, title: "Commercial Solar Film", description: "Reduce HVAC load and cut glare across open floor plans and office buildings." },
    { icon: Eye, title: "Frosted & Branded Film", description: "Privacy for meeting rooms and branded graphics for storefronts — one clean solution." },
    { icon: Sparkles, title: "Anti-Graffiti Protection", description: "Sacrificial film layers that can be peeled and replaced — saving glass replacement costs." },
    { icon: Building2, title: "Multi-Site Rollouts", description: "Consistent finish and timeline across multiple locations, retail chains, or office towers." },
    { icon: Layers, title: "Bomb Blast Mitigation", description: "High-spec safety films for sensitive or high-risk commercial facilities." },
];

const FAQS = [
    {
        q: "Can you work around business hours?",
        a: "Yes — we frequently schedule evenings and weekends so your operations aren't disrupted. We'll build a timeline that suits your location.",
    },
    {
        q: "Do you provide project management for multi-location rollouts?",
        a: "Absolutely. For retail chains, franchises, and property managers we provide a single point of contact, consistent pricing, and predictable delivery.",
    },
    {
        q: "Is commercial film energy-efficient?",
        a: "Yes. Solar control films reduce summer heat gain and can lower HVAC costs — especially valuable for sun-facing office and retail facades.",
    },
    {
        q: "What about branding and privacy for offices?",
        a: "We install custom frosted graphics, branded film patterns, and privacy film for boardrooms, reception areas, and meeting rooms.",
    },
];

export default function SecurityFilmsCommercial() {
    const [quoteOpen, setQuoteOpen] = useState(false);
    return (
        <>
            <SEO
                title="Commercial Window Films — Storefront & Office Security"
                description="Commercial-grade security, solar, anti-graffiti, and branded window films for storefronts, offices, and multi-site rollouts across the GTA."
                path="/security-films/commercial"
                keywords="commercial window film, storefront security film, office solar film, anti-graffiti film, branded frosted film Toronto"
                jsonLd={serviceJsonLd(
                    "Commercial Window Films",
                    "Commercial-grade security, solar, anti-graffiti, and branded window films for businesses across the GTA.",
                )}
            />
            <ServicePageHero
                eyebrow="Commercial Window Films"
                title="Business-grade window protection, installed with care."
                subtitle="From retail storefronts and office towers to warehouses and restaurants — we deliver professional-grade security, solar and branded films across the GTA."
                image="https://customer-assets.emergentagent.com/job_gtasecure-staging/artifacts/0z7ui3we_commercial%20installation%20gta%20security.png"
                defaultQuoteService="Commercial Window Films"
                bullets={[
                    "Deter break-ins, smash-and-grabs, and vandalism",
                    "Reduce HVAC costs with solar control films",
                    "Branded, frosted, and privacy film for any interior",
                    "Multi-site rollouts with consistent quality",
                ]}
            />

            <section className="section bg-white">
                <div className="container-x">
                    <div className="max-w-2xl">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> Commercial solutions
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Protection, performance, and brand presence.
                        </h2>
                    </div>
                    <div className="mt-12">
                        <FeatureGrid items={FEATURES} />
                    </div>
                </div>
            </section>

            <section className="section bg-[#FDFCFB]">
                <div className="container-x">
                    <div className="max-w-2xl">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> How we deliver
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            A process designed around your operations.
                        </h2>
                    </div>
                    <div className="mt-12">
                        <ProcessSteps
                            steps={[
                                { title: "Site survey & spec", description: "We assess glass type, orientation, and building use to spec the right film." },
                                { title: "Scheduled installation", description: "Installs happen evenings, weekends, or phased by zone — your choice." },
                                { title: "Warranty & support", description: "Manufacturer-backed warranties plus our workmanship guarantee on every square foot." },
                            ]}
                        />
                    </div>
                </div>
            </section>

            <section className="section bg-white">
                <div className="container-x grid lg:grid-cols-[1fr_1.3fr] gap-12">
                    <div>
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> FAQ
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Commercial film, answered.
                        </h2>
                    </div>
                    <Accordion type="single" collapsible className="w-full">
                        {FAQS.map((f, i) => (
                            <AccordionItem value={`c-${i}`} key={f.q} className="border-b border-gray-200">
                                <AccordionTrigger className="text-left font-semibold text-navy text-base md:text-lg py-5 hover:no-underline">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            <CTAStripe
                heading="Secure your storefront or office today."
                subheading="Free commercial assessment — tailored to your industry."
                ctaLabel="Get My Commercial Quote"
                onCta={() => setQuoteOpen(true)}
            />
            <QuoteDialog
                open={quoteOpen}
                onOpenChange={setQuoteOpen}
                defaultService="Commercial Window Films"
            />
        </>
    );
}
